import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  defaultHomeWidgetPrefs,
  HOME_WIDGET_LABELS,
  moveHomeWidget,
  replaceHomeWidget,
  toggleHomeWidgetHidden,
  type HomeMainWidgetId,
  type HomeWidgetPrefs,
} from "./home-widget-prefs";

type PanelView = "all" | "focus" | "replace";

/**
 * Home Customize overlay — dialog on desk, bottom sheet on phone (plan/50).
 * Gear opens focused; Replace… swaps catalog into the slot.
 */
export function HomeCustomizePanel(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefs: HomeWidgetPrefs;
  onPrefsChange: (next: HomeWidgetPrefs) => void;
  /** When set, open focused on that widget (gear entry). */
  focusId?: HomeMainWidgetId | null;
}) {
  const [view, setView] = useState<PanelView>("all");
  const [focusId, setFocusId] = useState<HomeMainWidgetId | null>(null);

  useEffect(() => {
    if (!props.open) return;
    if (props.focusId) {
      setFocusId(props.focusId);
      setView("focus");
    } else {
      setFocusId(null);
      setView("all");
    }
  }, [props.open, props.focusId]);

  function close() {
    props.onOpenChange(false);
  }

  const focusLabel = focusId ? HOME_WIDGET_LABELS[focusId] : null;
  const focusIndex = focusId ? props.prefs.order.indexOf(focusId) : -1;
  const focusHidden = focusId
    ? props.prefs.hidden.includes(focusId)
    : false;

  const title =
    view === "replace" && focusLabel
      ? `Replace ${focusLabel}`
      : view === "focus" && focusLabel
        ? focusLabel
        : "Customize home";

  const description =
    view === "replace"
      ? "Pick a widget for this slot. The current one is hidden."
      : view === "focus"
        ? "Settings for this home card. Replace swaps another widget into this slot."
        : "Show or hide home cards and change their order. Saved on this device.";

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[min(90dvh,40rem)] flex-col gap-0 overflow-hidden p-0",
          /* Phone: bottom sheet */
          "inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-b-none rounded-t-[var(--radius-xl)]",
          /* Desk: centered dialog */
          "sm:inset-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[min(100%-1.5rem,26rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[var(--radius-xl)]",
        )}
      >
        <div className="shrink-0 border-b border-[var(--color-border)] px-4 pb-3 pt-3 sm:pt-4">
          {/* Phone bottom-sheet grabber */}
          <div
            className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--color-border)] sm:hidden"
            aria-hidden
          />
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {view === "all" ? (
            <AllWidgetsView
              prefs={props.prefs}
              onPrefsChange={props.onPrefsChange}
              onFocus={(id) => {
                setFocusId(id);
                setView("focus");
              }}
            />
          ) : null}

          {view === "focus" && focusId ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!focusHidden}
                  onChange={() =>
                    props.onPrefsChange(
                      toggleHomeWidgetHidden(props.prefs, focusId),
                    )
                  }
                />
                <span>Show on home</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={focusIndex <= 0}
                  onClick={() =>
                    props.onPrefsChange(
                      moveHomeWidget(props.prefs, focusId, -1),
                    )
                  }
                >
                  Move up
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    focusIndex < 0 ||
                    focusIndex >= props.prefs.order.length - 1
                  }
                  onClick={() =>
                    props.onPrefsChange(
                      moveHomeWidget(props.prefs, focusId, 1),
                    )
                  }
                >
                  Move down
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setView("replace")}
              >
                Replace…
              </Button>
              <button
                type="button"
                className="mt-1 border-0 bg-transparent p-0 text-sm font-medium text-[var(--color-primary)]"
                onClick={() => setView("all")}
              >
                All widgets
              </button>
            </div>
          ) : null}

          {view === "replace" && focusId ? (
            <div className="space-y-2">
              <button
                type="button"
                className="mb-2 border-0 bg-transparent p-0 text-sm font-medium text-[var(--color-primary)]"
                onClick={() => setView("focus")}
              >
                ← Back
              </button>
              <ul className="m-0 list-none space-y-2 p-0">
                {props.prefs.order
                  .filter((id) => id !== focusId)
                  .map((id) => {
                    const hidden = props.prefs.hidden.includes(id);
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2.5 text-left text-sm shadow-[var(--shadow-control)]"
                          onClick={() => {
                            props.onPrefsChange(
                              replaceHomeWidget(props.prefs, focusId, id),
                            );
                            setFocusId(id);
                            setView("focus");
                          }}
                        >
                          <span className="font-medium text-[var(--color-foreground)]">
                            {HOME_WIDGET_LABELS[id]}
                          </span>
                          {hidden ? (
                            <span className="text-xs text-[var(--color-muted)]">
                              Hidden
                            </span>
                          ) : (
                            <span className="text-xs text-[var(--color-muted)]">
                              On home
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => props.onPrefsChange(defaultHomeWidgetPrefs())}
          >
            Reset layout
          </Button>
          <Button type="button" size="sm" onClick={close}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AllWidgetsView(props: {
  prefs: HomeWidgetPrefs;
  onPrefsChange: (next: HomeWidgetPrefs) => void;
  onFocus: (id: HomeMainWidgetId) => void;
}) {
  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {props.prefs.order.map((id, index) => {
        const hidden = props.prefs.hidden.includes(id);
        return (
          <li
            key={id}
            className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border)]/70 px-3 py-2"
          >
            <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!hidden}
                onChange={() =>
                  props.onPrefsChange(toggleHomeWidgetHidden(props.prefs, id))
                }
              />
              <button
                type="button"
                className="border-0 bg-transparent p-0 text-left font-medium text-[var(--color-foreground)]"
                onClick={() => props.onFocus(id)}
              >
                {HOME_WIDGET_LABELS[id]}
              </button>
            </label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={index === 0}
              aria-label={`Move ${HOME_WIDGET_LABELS[id]} up`}
              onClick={() =>
                props.onPrefsChange(moveHomeWidget(props.prefs, id, -1))
              }
            >
              ↑
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={index === props.prefs.order.length - 1}
              aria-label={`Move ${HOME_WIDGET_LABELS[id]} down`}
              onClick={() =>
                props.onPrefsChange(moveHomeWidget(props.prefs, id, 1))
              }
            >
              ↓
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
